import React from "react";

const TooValuableVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson shows how to become valuable enough that your exit would hurt, before you ever signal options. It gives three tactics: build small dependencies through a crisp weekly rhythm people rely on, get stakeholders to spend a visible chip on you so they feel invested, and make them co author outcomes by letting them choose, name, and shape the work. The idea is to create real reliance and shared ownership so your absence feels costly.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-5 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>You can’t pull the fire alarm if there’s no smoke.</p>

    <p>
      So, before you hint at leaving, you have to build the kind of value that
      makes people panic at the thought of your exit.
    </p>

    <p>How do we do that?</p>

    <p>
      Let’s look at three specific tactics that promotable people use, often
      without even realizing it.
    </p>

    <p>
      <strong>TACTIC 1: BUILD SMALL DEPENDENCIES</strong>
    </p>

    <p>
      <strong>
        Create a rhythm where your work becomes a system they rely on.
      </strong>
    </p>

    <p>
      Your weekly update should be their Monday caffeine. When they can’t
      imagine starting the week without your input, you’re indispensable.
    </p>

    <p>
      Run a weekly 10-minute cadence they expect, status, risks, next steps.
      Make it crisp, valuable, and predictable. Personalize items and reports
      specifically for them. Not generic updates, things formatted exactly how
      they like, with the insights they care about.
    </p>

    <p>
      <strong>TACTIC 2: LET THEM SPEND A CHIP ON YOU</strong>
    </p>

    <p>
      <strong>People protect what they've publicly sponsored.</strong>
    </p>

    <p>
      When someone vouches for you, gives you a budget, or introduces you to
      their network, they've spent social capital on you.
    </p>

    <p>
      Ask for a small, visible bet: an introduction to a senior leader, a
      budget approval for a pilot, leading a cross-team initiative.
    </p>

    <p>
      Once they've spent that chip, they're invested. Your success becomes
      tied to their judgment. <br /> Losing you would mean their bet failed. And that
      means loss aversion triggered.
    </p>

    <p>Think of it like this: 
      <br />
      <p>
      A manager hires someone for a sales role. Turns out, the person isn’t
      naturally wired for sales.
      <br />
      They try operations, shaky.
      <br />
      They try support, still <em>not a fit.</em>
    </p>
    </p>



    <p>
      But because the manager made the hiring call, they double down.
      <br />
      “I’ll train them myself. They’ll get there.”
    </p>

    <p>
      That’s not logic. That’s ownership.
      <br />
      Once someone’s reputation is attached to you, they’d rather fix the bet
      than admit it didn’t land.
    </p>

    <p>
      <strong>TACTIC 3: MAKE THEM CO-AUTHOR THE OUTCOME</strong>
    </p>

    <p>
      <strong>
        Ownership starts when they've put their name, idea, or judgment into
        your work.
      </strong>
    </p>

    <p>
      People value things more when they've had a hand in creating them.
    </p>

    <p><strong>Let them</strong></p>

    <ol className="list-decimal list-inside space-y-2 pl-5">
      <li>
        <strong>Choose it:</strong> “Would you prioritize metric A or B for
        this dashboard?” They make the call, now it's partly theirs.
      </li>
      <li>
        <strong>Rename it:</strong> “What should we call this initiative?”
        When they name it, they own it.
      </li>
      <li>
        <strong>And you credit their support publicly:</strong> “Thanks to
        their guidance and resource support, we delivered this project ahead
        of schedule.”
      </li>
    </ol>

    <p>
      <strong>Position yourself as part of their team success:</strong> Frame
      your work as critical to the manager's own goals, not just your personal
      achievements.
    </p>

    <p>
      When your manager feels responsible for your successes and growth,
      losing you feels like losing part of their own track record.
    </p>

    <p>So here's the strategy in one sentence:</p>

    <p>
      Build enough value that losing you would hurt. Create dependencies so
      they rely on you. Let them invest in your success. And then — only then
      — give them a subtle reminder that you have options.
    </p>

    <p>
      Make them realize what they’d lose <em>before they lose you.</em> That’s
      when loss aversion kicks in. And suddenly, your promotion isn't just
      deserved, it's urgent.
    </p>

    <p>
      You just need to make your absence feel like breaking a habit they rely
      on.
    </p>
  </div>
);


  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default TooValuableVideo;
